/****************************************************************************************************
 * * TYPEOGRAPHY ELEMENTS RENDERER
 * --------------------------------------------------------------------------------------------------
 * This file is responsible for rendering the typography elements in the portable
 * and anywhere that the main serializer is used to process the typography elements.
 * **************************************************************************************************/
import React from 'react';
import { Typography, useTheme, Theme } from '@mui/material';

const DefaultTypography = (node: any) => {
  // If there is no node or no children, return null to avoid an app error.
  if (!node || !node.children) return null;

  // Get the current theme

  /********************************************************************************
   * * VARIANT
   * ------------------------------------------------------------------------------
   * Get the variant from the node. If the variant is not found, default to body2.
   * ******************************************************************************/
  let variant: string = node?.node?.style ?? 'body2';
  if (variant === 'normal') variant = 'body2';

  /********************************************************************************
   * * COLOR
   * ------------------------------------------------------------------------------
   * Get the color from the theme. If the color is not found, default to textPrimary.
   * NOTE: In the CMS a color can be set for each typography element. This is not
   * a standard theme item, but is a custom theme item that we / I added. Therfore
   * it is not automatically passed to the typography component by the theme. This
   * is why it is manually extracted from the theme object and applied here.
   * ******************************************************************************/
  const theme: Theme = useTheme();
  const color = (theme.typography as any)[variant]?.color || 'textPrimary';

  switch (variant) {
    /********************************************************************************
     * * h1 to h4
     * ------------------------------------------------------------------------------
     * Render these heading elements as the default html header variants. This will
     * allow the typography to be styled as a header element and will also allow the
     * SEO to be improved by using the correct header elements. With header elements
     * it's important that they are used in a cascading manner for SEO purposes.
     * ******************************************************************************/
    case 'h1':
    case 'h2':
    case 'h3':
      return (
        <Typography color={color} variant={variant} width={'100%'}>
          {node.children}
        </Typography>
      );
      /********************************************************************************
       * * h5 to h6
       * ------------------------------------------------------------------------------
       * These headers render as div elements rather than header elements. This is
       * because they are not as important as the h1 to h4 elements. They will render
       * according to the theme, but are mapped to a div to avoid any SEO issues with
       * header cascading.
       * ******************************************************************************/
    case 'h4':
    case 'h5':
    case 'h6':
      return (
        <Typography color={color} variant={variant} component={'div'} width={'100%'}>
          {node.children}
        </Typography>
      );

    /********************************************************************************
     * * BODY1 AND BODY2
     * ------------------------------------------------------------------------------
     * These render normally, with the exception that they are given a paragraph
     * and gutterBottom by default. This is because they are the most common
     * typography elements and are often used in a paragraph format.
     * ******************************************************************************/
    case 'body1':
    case 'body2':
      return (
        <Typography          
          color={color}
          variant={variant}
          component="div"
          width={'100%'}
        >
          {node.children}
        </Typography>
      );
    /********************************************************************************
     * * SUBTITLE1, SUBTITLE2, CAPTION, OVERLINE
     * ------------------------------------------------------------------------------
     * These render normally but are logicall separated from the body1 and body2
     * so that in the future we can modify these styles independently.
     * ******************************************************************************/
    case 'subtitle1':
    case 'subtitle2':
    case 'caption':
    case 'overline':
      return (
        <Typography          
          color={color}
          variant={variant}
          component="div"
          width={'100%'}
        >
          {node.children}
        </Typography>
      );
    /********************************************************************************
     * * BUTTON
     * ------------------------------------------------------------------------------
     * The button element renders per normal themed behavior.
     * ******************************************************************************/
    case 'button':
      return (
        <Typography color={color} variant={variant} component="div" width={'100%'}>
          {node.children}
        </Typography>
      );

    /********************************************************************************
     * * UNRECOGNIZED
     * ------------------------------------------------------------------------------
     * Any unrecognized typography will render as a body2 element. This is to ensure
     * that the typography is always rendered and that the user is not left with
     * missing typography.
     * ******************************************************************************/
    default:
      return (
        <Typography color={color} variant={'body2'} component="div" width={'100%'}>
          {node.children}
        </Typography>
      );
  }
};

export default DefaultTypography;
